import type { BimWorkOrderOtherPriceData, WorkOrderStatusEnum } from 'gql/graphql';
import { ProjectMethodologyEnum } from 'gql/graphql';
import { OtherViewModal } from 'pages/Project/Common/Contract/OtherView';
import { Button, ContextMenu, IconKebab } from 'uikit';

import * as R from 'ramda';
import { useCallback, useMemo, useRef, useState } from 'react';

import { BimOtherPriceModal } from '../BimOtherPriceModal';

import { BimWorkOrderDistributionModal } from './BimWorkOrderDistributionModal';
import { useBimWorkOrderOtherTableRowMenu } from './hooks/useBimWorkOrderOtherTableRowMenu';

interface IBimWorkOrderOtherTableRowMenu {
    statusWorkOrder: WorkOrderStatusEnum;
    isFrameContract: boolean;
    refreshWorkOrderOtherPriceList: () => void;
    openSingleDeleteModalHandler: () => void;
    constructionTypeId?: number;
    dataRow?: BimWorkOrderOtherPriceData;
}

const getItemLabel = R.prop('label');
const getItemOnClick = R.prop('action');

export const BimWorkOrderOtherTableRowMenu = (props: IBimWorkOrderOtherTableRowMenu) => {
    const {
        constructionTypeId,
        dataRow,
        statusWorkOrder,
        openSingleDeleteModalHandler,
        refreshWorkOrderOtherPriceList,
        isFrameContract,
    } = props;
    const contextMenuRef = useRef(null);

    const [isOpen, setIsOpen] = useState(false);
    const [isOpenDistributionModal, setIsOpenDistributionModal] = useState<boolean>(false);
    const [isOpenViewModal, setIsOpenViewModal] = useState<boolean>(false);

    const handleOpenDistributionModal = useCallback(() => {
        setIsOpenDistributionModal(true);
    }, []);

    const handleCloseDistributionModal = useCallback(() => {
        setIsOpenDistributionModal(false);
    }, []);

    const handleOpenViewModal = useCallback(() => {
        setIsOpenViewModal(true);
    }, []);

    const handleCloseViewModal = useCallback(() => {
        setIsOpenViewModal(false);
    }, []);

    const [isOpenEditModal, setIsOpenEditModal] = useState<boolean>(false);

    const handleOnClick = useCallback(() => {
        setIsOpen((prev) => !prev);
    }, []);

    const handleItemOnClick = useCallback(
        (item) => () => {
            const action = getItemOnClick(item) as () => void;

            action();
            setIsOpen(false);
        },
        [],
    );

    const handleOpenEditModal = useCallback(() => {
        setIsOpenEditModal(true);
    }, []);

    const handleCloseEditModal = useCallback(() => {
        setIsOpenEditModal(false);
    }, []);

    const { items } = useBimWorkOrderOtherTableRowMenu({
        openSingleDeleteModalHandler,
        handleOpenDistributionModal,
        handleOpenEditModal,
        handleOpenViewModal,
        dataRow,
        statusWorkOrder,
        isFrameContract,
    });

    const isExcludedRow = useMemo(() => Boolean(dataRow?.isExcludedRow), [dataRow?.isExcludedRow]);

    return (
        <>
            <Button
                iconLeft={IconKebab}
                onlyIcon
                view="clear"
                size="xs"
                onClick={handleOnClick}
                ref={contextMenuRef}
                disabled={isExcludedRow}
            />
            <ContextMenu
                isOpen={isOpen}
                items={items}
                getItemLabel={getItemLabel}
                anchorRef={contextMenuRef}
                direction="downStartLeft"
                getItemOnClick={handleItemOnClick}
                onClickOutside={handleOnClick}
            />
            {isOpenDistributionModal && (
                <BimWorkOrderDistributionModal
                    dataRow={dataRow}
                    isOpenModal={isOpenDistributionModal}
                    constructionTypeId={constructionTypeId}
                    statusWorkOrder={statusWorkOrder}
                    refreshTable={refreshWorkOrderOtherPriceList}
                    onCloseModal={handleCloseDistributionModal}
                    isFrameContract={isFrameContract}
                />
            )}
            {isOpenEditModal && (
                <BimOtherPriceModal
                    dataRow={dataRow}
                    constructionTypeId={constructionTypeId}
                    statusWorkOrder={statusWorkOrder}
                    isOpenModal={isOpenEditModal}
                    onCloseModal={handleCloseEditModal}
                    refreshTable={refreshWorkOrderOtherPriceList}
                />
            )}
            {isOpenViewModal && (
                <OtherViewModal
                    methodology={ProjectMethodologyEnum.Bim}
                    onClose={handleCloseViewModal}
                    workOrderOtherId={dataRow?.id}
                    costRounding={0}
                />
            )}
        </>
    );
};

BimWorkOrderOtherTableRowMenu.displayName = 'BimWorkOrderOtherTableRowMenu';
